import clsx from 'clsx'

import { Score } from '@/components/Score'
import { HubRoute } from '@/types/hub'
import { useRouteDetails } from '../../hooks/useRouteDetails'
import { Button } from '@/ui/Button'
import { useWorkstationStore } from '../../store'

import styles from './Routes.module.scss'

import InformationIcon from '../../assets/information.svg'
import ArrowIcon from '../../assets/arrow-external.svg'
import { useDateStore } from '@/components/DateInput'
import { isToday } from 'date-fns'

interface Props extends HubRoute {
    isReassigned: boolean
}

const LOW_FREQUENCY = 'LF'
const LRI_URL = import.meta.env.VITE_LRI_URL

const stringForFrequency = (value: string, frequency: string) => {
    if (frequency === LOW_FREQUENCY) {
        return `${(+value).toFixed(1)}%`
    }
    return `${(+value).toFixed(2)}`
}

export const RouteDetails = ({
    route,
    mps,
    variance,
    ewt,
    isInaccurate,
    actual,
    expected,
    routeFrequency,
    isReassigned,
}: Props) => {
    const { workstation } = useWorkstationStore()
    const { date } = useDateStore()
    const { routeMileageLossInfo, amountOfCurtailments, amountOfDisruptions } = useRouteDetails(route)

    const handleViewOnLRI = () => {
        window.open(`${LRI_URL}/workstation/${encodeURIComponent(workstation?.workstationNumber || '')}?Route=${route}`)
    }
    const isLowFrequencyRoute = routeFrequency === LOW_FREQUENCY

    const isGeneralInfoVisible = isToday(date)

    return (
        <div className={styles.details}>
            <div className={styles.detailsWrapper}>
                <div className={styles.routeInfo}>
                    <span className={styles.title}>{route}</span>
                    {isLowFrequencyRoute && <span className={styles.hint}>{routeFrequency}</span>}
                </div>
                {isReassigned && (
                    <div className={styles.reassignedInfo}>
                        <span>
                            <InformationIcon />
                        </span>
                        <p>Route reassigned in 10 minutes</p>
                    </div>
                )}
                {isInaccurate && (
                    <p>*There's a problem with this data source. Please be careful when using this data.</p>
                )}
            </div>
            <div className={styles.statsContainer}>
                <div className={styles.statsRow}>
                    <span className={styles.label}>MPS</span>
                    <span className={styles.stats}>{stringForFrequency(mps, routeFrequency)}</span>
                </div>
                {isGeneralInfoVisible && (
                    <div className={styles.statsRow}>
                        <span className={styles.label}>EWT</span>
                        <Score
                            ewt={+ewt}
                            mps={+mps}
                            variance={+variance}
                            value={+ewt}
                            actual={actual}
                            expected={expected}
                            isInaccurate={isInaccurate}
                            isLowFrequency={isLowFrequencyRoute}
                        />
                    </div>
                )}
                {isGeneralInfoVisible && (
                    <div className={styles.statsRow}>
                        <span className={styles.label}>Variance</span>
                        <span className={styles.stats}>{stringForFrequency(variance, routeFrequency)}</span>
                    </div>
                )}
                <div className={clsx(styles.statsRow, styles.disruptionRow)}>
                    <span className={styles.label}>Disruptions</span>
                    <span className={styles.detailsValue}>{amountOfDisruptions}</span>
                </div>
                <div className={styles.statsRow}>
                    <span className={styles.label}>Curtailments</span>
                    <span className={styles.detailsValue}>{amountOfCurtailments}</span>
                </div>
            </div>
            <div className={styles.mileageLossContainer}>
                <div className={styles.statsRow}>
                    <span className={styles.label}>Mileage loss</span>
                    <span className={styles.detailsValue}>{routeMileageLossInfo.total.toFixed(2)}%</span>
                </div>
                <div className={styles.mileageLossRow}>
                    <div className={clsx(styles.mileageLossItem, styles.mileageLossT)}>
                        <span className={styles.mileageLossLabel}>T</span>
                        <span className={clsx(styles.mileageLossValue, styles.mileageValueAndPercent)}>
                            {routeMileageLossInfo.mileageLoss.T.toFixed(2)}
                            <span className={styles.mileagePercentLabel}>%</span>
                        </span>
                    </div>
                    <div className={clsx(styles.mileageLossItem, styles.mileageLossM)}>
                        <span className={styles.mileageLossLabel}>M</span>
                        <span className={clsx(styles.mileageLossValue, styles.mileageValueAndPercent)}>
                            {routeMileageLossInfo.mileageLoss.M.toFixed(2)}
                            <span className={styles.mileagePercentLabel}>%</span>
                        </span>
                    </div>
                    <div className={clsx(styles.mileageLossItem, styles.mileageLossOD)}>
                        <span className={styles.mileageLossLabel}>OD</span>
                        <span className={clsx(styles.mileageLossValue, styles.mileageValueAndPercent)}>
                            {routeMileageLossInfo.mileageLoss.OD.toFixed(2)}
                            <span className={styles.mileagePercentLabel}>%</span>
                        </span>
                    </div>
                </div>
            </div>
            {isGeneralInfoVisible && (
                <div className={styles.actionContainer}>
                    <Button className={styles.action} variant="transparent" size="small" onClick={handleViewOnLRI}>
                        <ArrowIcon />
                        View on LRI
                    </Button>
                </div>
            )}
        </div>
    )
}
